import React, { useEffect, useState } from "react";
import { Table, Tag, Space, Input, Select, Typography, Spin } from "antd";
import { UserOutlined, SearchOutlined, ClockCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { saasControlService, UserSession } from "~/services/saas-control";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(duration);
dayjs.extend(relativeTime);

const { Text } = Typography;
const { Option } = Select;

export const SessionsTab: React.FC = () => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const data = await saasControlService.getSessions();
      setSessions(data);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (loginAt: string, logoutAt: string | null) => {
    const start = dayjs(loginAt);
    const end = logoutAt ? dayjs(logoutAt) : dayjs();
    const diff = end.diff(start);
    const dur = dayjs.duration(diff);

    if (dur.asMinutes() < 60) {
      return `${Math.round(dur.asMinutes())} min`;
    } else if (dur.asHours() < 24) {
      return `${Math.round(dur.asHours())} hrs`;
    } else {
      return `${Math.round(dur.asDays())} days`;
    }
  };

  const columns: ColumnsType<UserSession> = [
    {
      title: "User",
      key: "user",
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div>{record.user.username}</div>
            <Text type="secondary" className="text-xs">
              {record.user.email || "No email"}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Login Time",
      dataIndex: "loginAt",
      key: "loginAt",
      render: (date) => (
        <div>
          <div>{dayjs(date).format("MMM D, YYYY h:mm A")}</div>
          <Text type="secondary" className="text-xs">
            {dayjs(date).fromNow()}
          </Text>
        </div>
      ),
      sorter: (a, b) =>
        new Date(a.loginAt).getTime() - new Date(b.loginAt).getTime(),
      defaultSortOrder: "descend",
    },
    {
      title: "Logout Time",
      dataIndex: "logoutAt",
      key: "logoutAt",
      render: (date) =>
        date ? (
          <div>
            <div>{dayjs(date).format("MMM D, YYYY h:mm A")}</div>
            <Text type="secondary" className="text-xs">
              {dayjs(date).fromNow()}
            </Text>
          </div>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, record) => (
        <Space>
          <ClockCircleOutlined />
          {formatDuration(record.loginAt, record.logoutAt)}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>
          {isActive ? "Active" : "Ended"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Ended", value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      render: (ip) => (
        <Text type="secondary" className="font-mono text-xs">
          {ip || "Unknown"}
        </Text>
      ),
    },
    {
      title: "Device",
      dataIndex: "userAgent",
      key: "userAgent",
      render: (ua) => {
        if (!ua) return <Text type="secondary">Unknown</Text>;

        // Simple parsing of user agent
        let device = "Unknown";
        if (ua.includes("iPhone")) device = "iPhone";
        else if (ua.includes("Android")) device = "Android";
        else if (ua.includes("Windows")) device = "Windows";
        else if (ua.includes("Mac")) device = "Mac";
        else if (ua.includes("Linux")) device = "Linux";

        return (
          <Text type="secondary" className="text-xs">
            {device}
          </Text>
        );
      },
      ellipsis: true,
    },
  ];

  const filteredSessions = sessions.filter((session) => {
    const searchLower = search.toLowerCase();
    const matchesSearch =
      session.user.username.toLowerCase().includes(searchLower) ||
      session.user.email?.toLowerCase().includes(searchLower) ||
      session.ipAddress?.toLowerCase().includes(searchLower);

    const matchesStatus =
      statusFilter === undefined ||
      (statusFilter === "active" && session.isActive) ||
      (statusFilter === "ended" && !session.isActive);

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex gap-4">
        <Input
          placeholder="Search by user or IP..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Select
          placeholder="Filter by status"
          value={statusFilter}
          onChange={setStatusFilter}
          allowClear
          style={{ width: 150 }}
        >
          <Option value="active">Active</Option>
          <Option value="ended">Ended</Option>
        </Select>
      </div>
      <Table
        columns={columns}
        dataSource={filteredSessions}
        rowKey="id"
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} sessions`,
        }}
      />
    </div>
  );
};
